import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import {
  Users as UsersIcon,
  UserPlus,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Edit,
  Trash2,
  Key,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  X,
  Search,
  RefreshCw,
  History,
  ChefHat,
  Wallet,
  Package,
  UtensilsCrossed,
  Crown,
  Building2
} from 'lucide-react';

const ROLE_CONFIG = {
  admin: { 
    icon: Crown, 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    badgeColor: 'bg-purple-500'
  },
  gerente: { 
    icon: ShieldCheck, 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    badgeColor: 'bg-blue-500'
  },
  cajero: { 
    icon: Wallet, 
    color: 'bg-green-100 text-green-700 border-green-200',
    badgeColor: 'bg-green-500'
  },
  mesero: { 
    icon: UtensilsCrossed, 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    badgeColor: 'bg-orange-500'
  },
  cocinero: { 
    icon: ChefHat, 
    color: 'bg-red-100 text-red-700 border-red-200',
    badgeColor: 'bg-red-500'
  },
  almacenista: { 
    icon: Package, 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    badgeColor: 'bg-amber-500'
  }
};

const Users = () => {
  const { api, user } = useAuth();
  const { branches } = useBranch();
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('users'); // users, audit
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'mesero',
    pin: '',
    phone: '',
    branch_id: ''  // Sucursal asignada
  });
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [staffRes, rolesRes] = await Promise.all([
        api.get('/users/staff'),
        api.get('/users/roles')
      ]);
      setStaff(staffRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para ver esta página');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await api.get('/users/audit-logs?limit=100');
      setAuditLogs(res.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingUser) {
        // Update existing user
        await api.put(`/users/staff/${editingUser.user_id}`, {
          name: formData.name,
          role: formData.role,
          pin: formData.pin || null,
          phone: formData.phone || null,
          is_active: formData.is_active,
          branch_id: formData.branch_id || null  // Sucursal asignada
        });
        toast.success('Usuario actualizado');
        setShowModal(false);
        resetForm();
      } else {
        // Create new user
        if (!formData.email || !formData.password || !formData.name) {
          toast.error('Completa todos los campos requeridos');
          return;
        }
        const res = await api.post('/users/staff', {
          ...formData,
          branch_id: formData.branch_id || null
        });
        
        // Show credentials modal
        const assignedBranch = branches.find(b => b.branch_id === formData.branch_id);
        setCreatedCredentials({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          pin: formData.pin || null,
          role: roles.find(r => r.id === formData.role)?.name || formData.role,
          branch: assignedBranch?.name || 'Todas (Admin)'
        });
        setShowModal(false);
        setShowCredentialsModal(true);
        resetForm();
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const copyCredentials = () => {
    if (!createdCredentials) return;
    const text = `👤 Usuario: ${createdCredentials.name}
📧 Email: ${createdCredentials.email}
🔑 Contraseña: ${createdCredentials.password}
${createdCredentials.pin ? `📌 PIN: ${createdCredentials.pin}` : ''}
👔 Rol: ${createdCredentials.role}
🏪 Sucursal: ${createdCredentials.branch || 'Todas (Admin)'}
🔗 Link: ${window.location.origin}`;
    
    navigator.clipboard.writeText(text);
    toast.success('Credenciales copiadas al portapapeles');
  };

  const handleDelete = async (staffId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await api.delete(`/users/staff/${staffId}`);
      toast.success('Usuario eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleToggleActive = async (staffMember) => {
    try {
      await api.put(`/users/staff/${staffMember.user_id}`, {
        is_active: !staffMember.is_active
      });
      toast.success(staffMember.is_active ? 'Usuario desactivado' : 'Usuario activado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      await api.post(`/users/change-password/${selectedUserForPassword.user_id}`, {
        new_password: newPassword
      });
      toast.success('Contraseña actualizada');
      setShowPasswordModal(false);
      setSelectedUserForPassword(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const openEditModal = (staffMember) => {
    setEditingUser(staffMember);
    setFormData({
      email: staffMember.email,
      password: '',
      name: staffMember.name,
      role: staffMember.role,
      pin: '',
      phone: staffMember.phone || '',
      is_active: staffMember.is_active,
      branch_id: staffMember.branch_id || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'mesero',
      pin: '',
      phone: '',
      branch_id: ''
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredStaff = staff.filter(s => 
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.role_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if current user is admin
  const isAdmin = user?.role === 'admin';

  if (!isAdmin && user?.role !== 'gerente') {
    return (
      <div className="min-h-screen bg-background flex">
        <Sidebar />
        <main className="flex-1 md:ml-64 p-8 pt-20 md:pt-8">
          <div className="max-w-2xl mx-auto text-center py-20">
            <ShieldAlert className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Acceso Denegado</h1>
            <p className="text-muted-foreground">No tienes permisos para acceder a esta sección.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-3 md:p-8 pt-16 md:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="p-2 rounded-lg bg-accent/10">
                  <UsersIcon className="w-5 h-5 md:w-6 md:h-6 text-accent" />
                </div>
                <div>
                  <h1 className="text-xl md:text-3xl font-chivo font-extrabold" data-testid="users-title">
                    Usuarios
                  </h1>
                  <p className="text-muted-foreground text-xs md:text-sm hidden sm:block">
                    Roles y permisos del equipo
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={fetchData} className="h-9 w-9">
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                {isAdmin && (
                  <Button 
                    className="bg-accent text-white h-9 px-3"
                    onClick={() => { resetForm(); setShowModal(true); }}
                    data-testid="add-user-btn"
                  >
                    <UserPlus className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Nuevo</span>
                  </Button>
                )}
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setActiveTab('users')}
                className={`flex-1 md:flex-initial flex items-center justify-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                  activeTab === 'users' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <UsersIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                Usuarios ({staff.length})
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`flex-1 md:flex-initial flex items-center justify-center gap-1.5 px-3 md:px-4 py-2 rounded-md text-xs md:text-sm font-medium transition-colors ${
                  activeTab === 'audit' ? 'bg-white shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <History className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span className="hidden sm:inline">Historial</span>
                <span className="sm:hidden">Log</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
            </div>
          ) : activeTab === 'users' ? (
            <div className="space-y-4">
              {/* Role Cards Summary - Scrollable on mobile */}
              <div className="flex gap-2 overflow-x-auto pb-2 md:grid md:grid-cols-3 lg:grid-cols-6 md:gap-3 md:overflow-visible scrollbar-hide">
                {roles.map(role => {
                  const config = ROLE_CONFIG[role.id] || ROLE_CONFIG.mesero;
                  const RoleIcon = config.icon;
                  const count = staff.filter(s => s.role === role.id).length;
                  
                  return (
                    <Card key={role.id} className={`border ${config.color} flex-shrink-0 w-24 md:w-auto`}>
                      <CardContent className="p-2 md:p-3 text-center">
                        <RoleIcon className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1" />
                        <p className="font-bold text-base md:text-lg">{count}</p>
                        <p className="text-[10px] md:text-xs truncate">{role.name}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-users"
                />
              </div>

              {/* Users - Cards on mobile, Table on desktop */}
              <div className="md:hidden space-y-3">
                {filteredStaff.map((member) => {
                  const config = ROLE_CONFIG[member.role] || ROLE_CONFIG.mesero;
                  const RoleIcon = config.icon;
                  const isCurrentUser = member.user_id === user?.user_id;
                  
                  return (
                    <Card key={member.user_id} className={`${!member.is_active ? 'opacity-50' : ''}`}>
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.badgeColor}`}>
                              <span className="text-white font-bold">
                                {member.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {member.name}
                                {isCurrentUser && (
                                  <span className="text-[10px] bg-accent/20 text-accent px-1.5 py-0.5 rounded-full">Tú</span>
                                )}
                              </p>
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                              <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${config.color}`}>
                                <RoleIcon className="w-3 h-3" />
                                {member.role_name}
                              </span>
                            </div>
                          </div>
                          {isAdmin && !isCurrentUser && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditModal(member)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedUserForPassword(member); setShowPasswordModal(true); }}>
                                <Key className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Users Table - Desktop only */}
              <Card className="border border-border/50 hidden md:block">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Rol</TableHead>
                        <TableHead>Sucursal</TableHead>
                        <TableHead>PIN</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Último Acceso</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredStaff.map((member) => {
                        const config = ROLE_CONFIG[member.role] || ROLE_CONFIG.mesero;
                        const RoleIcon = config.icon;
                        const isCurrentUser = member.user_id === user?.user_id;
                        const assignedBranch = branches.find(b => b.branch_id === member.branch_id);
                        
                        return (
                          <TableRow key={member.user_id} className={!member.is_active ? 'opacity-50' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.badgeColor}`}>
                                  <span className="text-white font-bold">
                                    {member.name?.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium flex items-center gap-2">
                                    {member.name}
                                    {isCurrentUser && (
                                      <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full">Tú</span>
                                    )}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{member.email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                                <RoleIcon className="w-3.5 h-3.5" />
                                {member.role_name}
                              </span>
                            </TableCell>
                            <TableCell>
                              {assignedBranch ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                                  assignedBranch.is_matriz 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  <Building2 className="w-3 h-3" />
                                  {assignedBranch.name}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">Todas</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {member.has_pin ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <Key className="w-4 h-4" />
                                  ****
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {member.phone ? (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                                  {member.phone}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {member.is_active ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  Activo
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-red-600">
                                  <XCircle className="w-4 h-4" />
                                  Inactivo
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {member.last_login ? formatDateTime(member.last_login) : 'Nunca'}
                            </TableCell>
                            <TableCell className="text-right">
                              {isAdmin && !isCurrentUser && (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditModal(member)}
                                    data-testid={`edit-user-${member.user_id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedUserForPassword(member);
                                      setShowPasswordModal(true);
                                    }}
                                  >
                                    <Key className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleActive(member)}
                                    className={member.is_active ? 'text-orange-600' : 'text-green-600'}
                                  >
                                    {member.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(member.user_id)}
                                    className="text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                              {isCurrentUser && (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {filteredStaff.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      {searchQuery ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            /* Audit Logs Tab */
            <Card className="border border-border/50">
              <CardHeader>
                <CardTitle className="font-chivo flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Historial de Acciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Acción</TableHead>
                      <TableHead>Detalles</TableHead>
                      <TableHead>Realizado por</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log.log_id}>
                        <TableCell className="text-sm">
                          {formatDateTime(log.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">{log.user_name}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-muted rounded text-xs font-medium">
                            {log.action_label}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                          {log.details ? JSON.stringify(log.details).substring(0, 50) : '-'}
                        </TableCell>
                        <TableCell>{log.performed_by_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {auditLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No hay registros de auditoría</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Create/Edit User Modal */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-accent text-white p-4 rounded-t-xl flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  {editingUser ? <Edit className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                  {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Content */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nombre completo *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Juan Pérez"
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  
                  {!editingUser && (
                    <>
                      <div className="col-span-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="usuario@email.com"
                          required
                          data-testid="user-email-input"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Contraseña *</Label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            placeholder="Mínimo 6 caracteres"
                            required
                            data-testid="user-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="col-span-2">
                    <Label>Rol *</Label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      data-testid="user-role-select"
                    >
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>
                          {role.name} - {role.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Sucursal asignada - Solo para roles que no son admin */}
                  <div className="col-span-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Sucursal Asignada
                    </Label>
                    <select
                      value={formData.branch_id}
                      onChange={(e) => setFormData({...formData, branch_id: e.target.value})}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      data-testid="user-branch-select"
                      disabled={formData.role === 'admin'}
                    >
                      <option value="">Sin asignar (puede ver todas)</option>
                      {branches.map(branch => (
                        <option key={branch.branch_id} value={branch.branch_id}>
                          {branch.name} {branch.is_matriz ? '(Matriz)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.role === 'admin' 
                        ? 'Los administradores pueden acceder a todas las sucursales'
                        : 'El usuario solo verá datos de esta sucursal al iniciar sesión'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <Label>PIN de acceso rápido</Label>
                    <Input
                      type="text"
                      value={formData.pin}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                        setFormData({...formData, pin: val});
                      }}
                      placeholder="4 dígitos"
                      maxLength={4}
                      data-testid="user-pin-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Para cambio rápido de turno</p>
                  </div>
                  
                  <div>
                    <Label>Teléfono</Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="10 dígitos"
                      data-testid="user-phone-input"
                    />
                  </div>
                  
                  {editingUser && (
                    <div className="col-span-2">
                      <Label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                          className="w-4 h-4"
                        />
                        Usuario activo
                      </Label>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-accent text-white" data-testid="save-user-btn">
                    {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showPasswordModal && selectedUserForPassword && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPasswordModal(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-blue-600 text-white p-4 rounded-t-xl flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Cambiar Contraseña
                </h2>
                <button onClick={() => setShowPasswordModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Cambiando contraseña para: <strong>{selectedUserForPassword.name}</strong>
                </p>
                
                <div>
                  <Label>Nueva Contraseña</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setShowPasswordModal(false)}>
                    Cancelar
                  </Button>
                  <Button className="flex-1 bg-blue-600 text-white" onClick={handleChangePassword}>
                    Cambiar Contraseña
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Credentials Modal - Shows after creating user */}
      <AnimatePresence>
        {showCredentialsModal && createdCredentials && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-md"
            >
              <div className="bg-green-600 text-white p-4 rounded-t-xl flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  ¡Usuario Creado!
                </h2>
                <button onClick={() => setShowCredentialsModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Guarda estas credenciales para compartir con el usuario:
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 font-mono text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">👤 Usuario:</span>
                    <span className="font-semibold">{createdCredentials.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">📧 Email:</span>
                    <span className="font-semibold">{createdCredentials.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">🔑 Contraseña:</span>
                    <span className="font-semibold">{createdCredentials.password}</span>
                  </div>
                  {createdCredentials.pin && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">📌 PIN:</span>
                      <span className="font-semibold">{createdCredentials.pin}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">👔 Rol:</span>
                    <span className="font-semibold">{createdCredentials.role}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">🏪 Sucursal:</span>
                    <span className="font-semibold">{createdCredentials.branch || 'Todas (Admin)'}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <span className="text-gray-500">🔗 Link:</span>
                    <span className="font-semibold text-xs break-all ml-2">{window.location.origin}</span>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1" 
                    onClick={copyCredentials}
                  >
                    📋 Copiar Todo
                  </Button>
                  <Button 
                    className="flex-1 bg-green-600 text-white" 
                    onClick={() => setShowCredentialsModal(false)}
                  >
                    Listo
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Users;
